import { EnrollmentStatus } from "@outschool/gql-backend-generated";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";

import { sortEnrollmentsByLearner } from "../../shared/Enrollment";
import {
  EnrollmentListByStatusQuery,
  EnrollmentListByStatusQueryVariables
} from "../../shared/generated/graphql";

export const EnrollmentListQuery = gql`
  query EnrollmentListByStatus($uid: ID!, $statusList: [EnrollmentStatus!]) {
    section(uid: $uid) {
      uid
      size_max
      activity {
        uid
        allowWaitlist
        is_ongoing_weekly
        hasTeacherSchedule
        isClub
        price_cents
        isSelfPaced
      }
      waitlistSeats {
        uid
        parent {
          uid
          name
        }
        learner {
          uid
          name
          age
          avatar
          isVerified
          newOnOutschool
        }
        previouslyEnrolled
        formerLearner
      }
      enrollments(statusList: $statusList) {
        uid
        isFirstOutschoolClass
        isRepeatLearnerOfTeacher
        parent {
          uid
          name
        }
        learner {
          uid
          name
          age
          avatar
          isVerified
          isBlocked
          newOnOutschool
        }
        otherSections {
          uid
        }
        createdAt
        isEnrolled
        isEnded
        isWithdrawn
        isPaused
        isStartingSoon
        ongoingStartTime
        pauseWindow {
          uid
          startDate
          endDate
          pauseReason
        }
      }
      unclaimedGiftEnrollments(statusList: $statusList) {
        uid
        buyer {
          uid
          name
        }
        createdAt
        isEnrolled
        isEnded
        isWithdrawn
        isPaused
      }
    }
  }
`;

export function useEnrollmentListQuery({
  uid,
  statusList
}: {
  uid: string;
  statusList: EnrollmentStatus[];
}) {
  const { loading, error, data, refetch } = useQueryWithPreviousData<
    EnrollmentListByStatusQuery,
    EnrollmentListByStatusQueryVariables
  >(EnrollmentListQuery, {
    fetchPolicy: "cache-and-network",
    variables: {
      uid,
      statusList
    }
  });

  const section = data?.section;
  const {
    enrollments = [],
    unclaimedGiftEnrollments = [],
    waitlistSeats = []
  } = section ?? {};

  const allEnrollments = [...enrollments, ...unclaimedGiftEnrollments];

  const sortedEnrollments = sortEnrollmentsByLearner(allEnrollments).filter(
    e =>
      (e.__typename === "Enrollment" && Boolean(e.learner)) ||
      e.__typename === "GiftEnrollment"
  );

  return {
    loading,
    error,
    refetch,
    section,
    waitlistSeats,
    enrollments: sortedEnrollments
  };
}

export function getEnrollmentListQueryVariables(sectionUid: $TSFixMe) {
  return {
    uid: sectionUid,
    statusList: [
      EnrollmentStatus.Ended,
      EnrollmentStatus.Withdrawn,
      EnrollmentStatus.Enrolled,
      EnrollmentStatus.Paused,
      EnrollmentStatus.StartingSoon
    ]
  };
}
